import { type FormEvent, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { SubmissionType } from '@gobingebox/shared';
import { cn } from '@/lib/cn';
import { PageHero } from '@/components/PagePlaceholder';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Input';
import { Lock } from '@/components/icons';
import { ApiError } from '@/lib/api';
import { devApi } from '@/features/dev/api';

const TYPES: { id: SubmissionType; label: string; titleLabel: string; titlePlaceholder: string }[] = [
  { id: 'show_idea', label: 'Pitch a show', titleLabel: 'Show title / concept', titlePlaceholder: 'Your show idea in a few words' },
  { id: 'upload_access', label: 'Request upload access', titleLabel: 'Your role / what you make', titlePlaceholder: 'e.g. Voice actor, animator, editor' },
];

function SubmissionForm() {
  const [type, setType] = useState<SubmissionType>('show_idea');
  const [form, setForm] = useState({ name: '', email: '', title: '', details: '', links: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const active = TYPES.find((t) => t.id === type)!;
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await devApi.submit({ type, ...form, links: form.links || undefined });
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <Alert tone="success">
        Thanks — your {type === 'show_idea' ? 'pitch' : 'request'} is in. We read every submission.
      </Alert>
    );
  }

  return (
    <div className="card p-6">
      <div className="mb-5 inline-flex rounded-full border border-border bg-surface p-1">
        {TYPES.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setType(t.id)}
            className={cn(
              'rounded-full px-4 py-1.5 text-sm font-semibold transition-colors',
              type === t.id ? 'bg-brand-gradient text-white' : 'text-ink-muted hover:text-ink',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        {error && <Alert tone="error">{error}</Alert>}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Your name" required value={form.name} onChange={set('name')} maxLength={80} />
          <Field label="Email" type="email" required value={form.email} onChange={set('email')} />
        </div>
        <Field
          label={active.titleLabel}
          required
          value={form.title}
          onChange={set('title')}
          placeholder={active.titlePlaceholder}
          maxLength={120}
        />
        <div className="space-y-1.5">
          <label htmlFor="details" className="block text-sm font-medium text-ink">
            {type === 'show_idea' ? 'Tell us about it' : 'Tell us about your work'}
          </label>
          <textarea
            id="details"
            required
            rows={5}
            maxLength={4000}
            value={form.details}
            onChange={set('details')}
            className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-ink placeholder:text-ink-faint focus:border-brand"
            placeholder={type === 'show_idea' ? 'Premise, tone, characters…' : 'What you’d contribute, experience, etc.'}
          />
        </div>
        <Field
          label="Links (optional)"
          value={form.links}
          onChange={set('links')}
          placeholder="Portfolio, reel, socials…"
          maxLength={500}
        />
        <Button type="submit" size="lg" disabled={busy}>
          {busy ? 'Sending…' : active.label}
        </Button>
        <p className="text-xs text-ink-faint">
          Outside uploads are reviewed against our content policy before anything is published.
        </p>
      </form>
    </div>
  );
}

export function DevForumsPage() {
  const { data, isLoading } = useQuery({ queryKey: ['dev-status'], queryFn: devApi.status });

  return (
    <div>
      <PageHero
        eyebrow="Dev Forums"
        title="Help shape what Nullify makes next."
        subtitle="BingeBox is a tiny studio — a solo founder and volunteer voice actors. If you've got a show idea or you want to contribute, this is the door."
      />
      <div className="container-page grid gap-8 pb-16 lg:grid-cols-[1fr_1.1fr]">
        <section className="space-y-4 text-ink-muted">
          <h2 className="font-display text-lg font-bold text-ink">About the studio</h2>
          <p>
            Every BingeBox original is produced in-house in both Japanese and English audio. We're
            always looking for collaborators and ideas worth making.
          </p>
          <p>
            <span className="font-semibold text-ink">Pitch a show</span> to send us a concept, or{' '}
            <span className="font-semibold text-ink">request upload access</span> if you'd like to
            contribute content. Approved contributors get access tools in the Dev Panel.
          </p>
        </section>

        <section>
          {isLoading ? (
            <p className="text-sm text-ink-faint">Loading…</p>
          ) : data && !data.open ? (
            <div className="card flex flex-col items-center gap-3 px-6 py-14 text-center">
              <div className="grid h-12 w-12 place-items-center rounded-2xl bg-surface-raised text-brand-soft">
                <Lock size={22} />
              </div>
              <h2 className="font-display text-lg font-bold text-ink">The Dev Forums are closed</h2>
              <p className="max-w-sm text-sm text-ink-muted">
                {data.message || 'Submissions are paused right now — check back soon.'}
              </p>
            </div>
          ) : (
            <SubmissionForm />
          )}
        </section>
      </div>
    </div>
  );
}
